/**
 * Cohort pivot — signup week × first channel × activation rate.
 *
 * Backend shape:
 *   { cohort: Array<{ cohort_week: string, first_channel: string | null,
 *                     activation_rate: string | number }> }
 * sourced from metrics.dim_user. activation_rate may arrive as a numeric
 * string (numeric(5,2)) from postgres — parseFloat both branches defensively.
 */

interface CohortRow {
  cohort_week: string;
  first_channel: string | null;
  activation_rate: string | number | null;
}

function parseRate(v: string | number | null): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function isoDate(v: string): string {
  // Normalize postgres timestamp to YYYY-MM-DD without TZ drift.
  // Backend returns date_trunc'd timestamps; slicing the ISO prefix is
  // safe + tz-neutral (the date_trunc'd day boundary is server-local).
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : String(v);
}

export function CohortTableCard({ data }: { data: { cohort: CohortRow[] } }) {
  const rows = data.cohort ?? [];
  if (rows.length === 0) {
    return (
      <section className="bg-card border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-1">Cohort: signup week × channel × activation rate</h3>
        <p className="text-sm text-muted-foreground">
          No cohort data yet. Source: metrics.dim_user.
        </p>
      </section>
    );
  }

  // Build pivot: unique weeks (most-recent first) × unique channels.
  const weeks: string[] = [];
  const channels: string[] = [];
  for (const r of rows) {
    if (!weeks.includes(r.cohort_week)) weeks.push(r.cohort_week);
    const c = r.first_channel ?? '__unattributed';
    if (!channels.includes(c)) channels.push(c);
  }
  // Sort weeks descending so the most-recent cohort sits at the top.
  weeks.sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  const recentWeeks = weeks.slice(0, 8);

  return (
    <section className="bg-card border rounded-lg p-6 overflow-x-auto">
      <h3 className="text-lg font-semibold mb-1">Cohort: signup week × channel × activation rate</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Activation rate = % of cohort who reached match-ready. Last 8 weeks.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b">
            <th className="py-2 pr-3 font-medium">Week</th>
            {channels.map((c) => (
              <th key={c} className="py-2 pr-3 font-medium">
                {c === '__unattributed' ? (
                  <span className="text-muted-foreground italic">Unattributed</span>
                ) : (
                  c
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {recentWeeks.map((w) => (
            <tr key={w} className="border-b last:border-0">
              <td className="py-2 pr-3 font-mono">{isoDate(w)}</td>
              {channels.map((c) => {
                const cell = rows.find(
                  (r) => r.cohort_week === w && (r.first_channel ?? '__unattributed') === c,
                );
                const rate = cell ? parseRate(cell.activation_rate) : null;
                return (
                  <td key={c} className="py-2 pr-3 font-mono">
                    {rate == null ? '—' : `${rate.toFixed(0)}%`}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
